// MACHINERY V2 (Stroje) — catalog browse page (M2.8).
//
// Spec: data package §11 in full. Reference impl:
// design_handoff_machinery_v3/machinery-catalog.jsx (`Catalog`);
// components/CatalogBrowsePage.tsx for the data flow. Artboards 0900
// (refined-claude) / 0901 (machineid-native) — one layout, two palettes.
//
// LAYOUT (>= 1280): rail 248px | main (breadcrumb, title + model search,
// category tabs, the showing-line, a 3-up card grid, compact pagination).
//
// >>> SPLIT AT M2.16c — WHAT LIVES WHERE NOW. <<< This file stood at 296 of the
// project's 300-line cap and the below-1280 pass had to add a whole second
// chrome to it, so the near-cap rule made a split the sub-phase's first task:
//   · CatalogShell.tsx    — the rail | main chrome AND the 1280 branch;
//   · CatalogOverlays.tsx — the page-level popup / add / legend / toast mounts.
// This file keeps what a page keeps: the hooks, the handlers and the decisions.
// The same seam fleet/FleetShell.tsx + fleet/FleetOverlays.tsx took from
// FleetPage.tsx, so the two browse surfaces are now shaped alike.
//
// >>> READINESS IS DERIVED ONCE PER MODEL, HERE. <<<
// `deriveReadinessPresentation` is the single entry point (M2.9, locked) and no
// component below re-derives anything. `ctx` is deliberately OMITTED: a real
// gateway connection only exists for a machine INSTANCE, never for a catalog
// model (midasReadiness.ts:83-90), so every catalog presentation comes back
// `connected: false` / `interactive: true` and the badges are clickable.
// The below-1280 card's readiness TIER is derived from that same object by
// readiness/readinessTier.ts — one more consumer of one derivation, never a
// second opinion on a raw field.
//
// >>> THE PAGE OWNS EVERY PIECE OF PENDING USER WORK (review findings F4 + F6).
// <<< `legendOpen` was already here; `sheetOpen` and the staged filter draft
// joined it, because CatalogShell UNMOUNTS the below-1280 chrome at 1280 and a
// pending eight-section selection is user work (useCatalogFilterDraft.ts's own
// rule (d) note). This is the shape CatalogAddDialogV2 already used for its form
// state — hook above the shell branch — now applied to the filter half too.
//
// AND EVERY WRITE TO `query` USES THE UPDATER FORM. The rail's range inputs
// publish on a 350 ms debounce and the sheet commits asynchronously, so a
// `{...query}` built from THIS render can spread a stale snapshot back over a
// value that landed since. catalogActiveFilters.ts:23-32 records the defect in
// full and fixed it for the chip row; these two handlers were the last render
// snapshots left on this surface.
//
// >>> THE OVERLAYS ARE MOUNTED AT PAGE LEVEL, NOT INSIDE A CARD. <<<
// Same rule as FleetPage.tsx:231-245: the popup must not be a React ancestor of
// a navigating card, or a click on its Close / backdrop / CTA re-enters the
// card's onClick. It also carries its own stopPropagation guards on both
// portalled siblings, which is what protects the in-card case. CatalogOverlays
// renders as a SIBLING of the rail/main row — keep it there.
//
// >>> aiProvenance "unknown": THE DELIBERATE-EYES PASS FOUND A REAL DEFECT. <<<
// The value is produced in ONE place (readinessPresentation.ts
// `aiProvenanceFrom` — "docs exist but no LIT lane names a source") and read in
// ONE place (CapabilityBadge's `tipCopy`). That reader tested only oem and
// direct and let EVERYTHING else fall into the fanout branch, so an "unknown"
// model's tooltip claimed "via the control-system package" — fabricating the
// provenance the member exists to withhold. Fixed in CapabilityBadge.tsx.
// THIS PAGE needed no branch and got none: it renders `summary.docPart`, which
// is provenance-independent. (M2.16c added a SECOND reader of that enum —
// readinessTier.ts — whose switch is exhaustive over all five members, so the
// same slip cannot repeat there: a new member fails to compile.)
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import usePermission from "../../../../shared/hooks/usePermission";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { V2_BELOW_DESKTOP, useV2MediaQuery } from "../useV2MediaQuery";
import { Modules } from "../../../../core/models/PermissionModels";
import { CatalogModelListItem } from "../../models/models";
import RetryNotice from "../feedback/RetryNotice";
import {
  deriveReadinessPresentation,
  readinessSummaryStrings,
} from "../readiness/readinessPresentation";
import CatalogModelCardMobile from "./CatalogModelCardMobile";
import CatalogModelCardV2 from "./CatalogModelCardV2";
import CatalogOverlays from "./CatalogOverlays";
import CatalogPagination from "./CatalogPagination";
import CatalogShell from "./CatalogShell";
import { CATALOG_V2_PATH, catalogAddedPath } from "./catalogChrome";
import { buildCatalogFacets } from "./catalogFacets";
import {
  catalogActiveFilterCount,
  clearCatalogFilters,
  setCatalogPage,
  setCatalogPageSize,
  withCatalogCategory,
} from "./catalogQuery";
import {
  CatalogEmpty,
  CatalogGrid,
  CatalogShowingLine,
  CatalogSkeletonGrid,
} from "./catalogBrowseParts";
import useCatalogFilterDraft from "./useCatalogFilterDraft";
import useCatalogBrowse from "./useCatalogBrowse";

const CatalogBrowseV2: React.FC = () => {
  const { translate } = useTranslation(["myMachinery", "common"]);
  const navigate = useNavigate();
  const { canWrite } = usePermission(Modules.Machinery);
  // F-A: below 1280 is read as the media QUERY both v2 stylesheets fence on,
  // never as an integer width. See useV2MediaQuery.ts.
  const belowDesktop = useV2MediaQuery(V2_BELOW_DESKTOP);
  const isDesktop = !belowDesktop;

  const c = useCatalogBrowse();
  const { query, setQuery } = c;

  const [brandsExpanded, setBrandsExpanded] = useState(false);
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());
  const [readinessModel, setReadinessModel] = useState<CatalogModelListItem | null>(null);
  const [addModel, setAddModel] = useState<CatalogModelListItem | null>(null);
  const [legendOpen, setLegendOpen] = useState(false);
  // F4: the sheet's open flag and its draft belong to the page, not to the
  // chrome the 1280 branch unmounts. See the header.
  const [sheetOpen, setSheetOpen] = useState(false);

  const summaryStrings = useMemo(() => readinessSummaryStrings(translate), [translate]);

  const facets = useMemo(
    () => buildCatalogFacets(c.manufacturers, c.brandSearch),
    [c.manufacturers, c.brandSearch]
  );

  const activeFilters = catalogActiveFilterCount(query);

  const toggleParent = useCallback((id: string) => {
    setExpandedParents((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  /** The brand OPTION-LIST state — the live search box and the two expanders.
   *  None of it is a query filter, all of it is reset by the same three verbs
   *  (tab change, Clear all, the sheet's Zrusit vsetko), so it is written once
   *  here and shared rather than restated at each of them. */
  const resetBrandFacetUi = () => {
    c.setBrandSearch("");
    setBrandsExpanded(false);
    setExpandedParents(new Set());
  };

  // F4: created HERE, above every breakpoint branch, so a rotation past 1280
  // cannot destroy a staged selection. `onClearBrandList` is what makes the
  // sheet's clear-all reset the same things the chip row's does (item 3).
  const filterApi = useCatalogFilterDraft({
    open: sheetOpen,
    query,
    setQuery,
    onClearBrandList: resetBrandFacetUi,
    // ONE expander for the rail and the sheet — they render the same list.
    brandsExpanded,
    setBrandsExpanded,
  });

  const onSelectCategory = (code?: string) => {
    // §11: a tab change resets the manufacturer facet AND the brand search box,
    // and returns to page 1. The facet reset lives in `withCatalogCategory`;
    // the brand box is local state, so it is cleared alongside.
    resetBrandFacetUi();
    // UPDATER FORM, NEVER A RENDER SNAPSHOT (F6) — a debounced range publish or
    // a sheet commit can land between this render and this tap, and
    // `withCatalogCategory(query, …)` would spread the pre-publish query back
    // over it. Same defect catalogActiveFilters.ts:23-32 documents.
    setQuery((prev) => withCatalogCategory(prev, code));
  };

  const onClearAll = () => {
    // `clearSearch`, not `setSearchValue("")`: emptying the box while a
    // keystroke is still in its 300ms debounce would let that keystroke
    // re-apply the search a moment after Clear all wiped it. The hook owns
    // cancel + box + committed value as one operation (useCatalogBrowse.ts).
    c.clearSearch();
    resetBrandFacetUi();
    // Updater form (F6) — see onSelectCategory.
    setQuery((prev) => clearCatalogFilters(prev));
  };

  const readinessPresentation = readinessModel
    ? deriveReadinessPresentation(readinessModel, undefined, summaryStrings)
    : null;

  return (
    <div style={{ display: "flex", flex: 1, minHeight: 0, minWidth: 0 }}>
      <CatalogShell
        c={c}
        facets={facets}
        brandsExpanded={brandsExpanded}
        onToggleBrandsExpanded={() => setBrandsExpanded((prev) => !prev)}
        expandedParents={expandedParents}
        onToggleParent={toggleParent}
        onSelectCategory={onSelectCategory}
        onClearAll={onClearAll}
        activeFilters={activeFilters}
        onOpenLegend={() => setLegendOpen(true)}
        sheetOpen={sheetOpen}
        onSheetOpenChange={setSheetOpen}
        filterApi={filterApi}
      >
        {/* §7 fix 5 for the browse surface — see useCatalogBrowse.ts. */}
        {c.failed && (
          <div style={{ marginBottom: 12 }}>
            <RetryNotice
              title={translate("myMachinery:v2.catalog.loadFailed")}
              hint={translate("myMachinery:v2.tabs.loadFailedHint")}
              onRetry={c.retry}
              busy={c.browseLoading}
            />
          </div>
        )}

        {c.browseLoading && c.browseResults.length === 0 ? (
          <CatalogSkeletonGrid />
        ) : c.browseResults.length === 0 ? (
          !c.failed && <CatalogEmpty hasFilters={activeFilters > 0} onClear={onClearAll} />
        ) : (
          <React.Fragment>
            <CatalogShowingLine
              page={query.page}
              pageSize={query.pageSize}
              totalCount={c.browseTotalCount}
            />

            <CatalogGrid>
              {c.browseResults.map((model) => {
                const presentation = deriveReadinessPresentation(
                  model,
                  undefined,
                  summaryStrings
                );
                // TWO CARDS, ONE DATA PATH. The desktop card is untouched by
                // M2.16c; the below-1280 card is a different LAYOUT, not a
                // resize, and both take the SAME derived presentation and the
                // SAME four callbacks — see CatalogModelCardMobile.tsx's header.
                return isDesktop ? (
                  <CatalogModelCardV2
                    key={model.id}
                    model={model}
                    presentation={presentation}
                    onOpen={(m) => navigate(`${CATALOG_V2_PATH}/${m.id}`)}
                    onOpenReadiness={setReadinessModel}
                    canWrite={canWrite}
                    onAdd={setAddModel}
                  />
                ) : (
                  <CatalogModelCardMobile
                    key={model.id}
                    model={model}
                    presentation={presentation}
                    onOpen={(m) => navigate(`${CATALOG_V2_PATH}/${m.id}`)}
                    onOpenReadiness={setReadinessModel}
                    canWrite={canWrite}
                    onAdd={setAddModel}
                  />
                );
              })}
            </CatalogGrid>

            {/* This strip renders at EVERY width — it is outside the desktop
                branch — so below 1280 its 28px controls fall under the two-axis
                >= 44px law (F5). `touch` is the only difference; at >= 1280 the
                prop is false and the component emits what it always did. */}
            <CatalogPagination
              page={query.page}
              pageSize={query.pageSize}
              totalCount={c.browseTotalCount}
              touch={belowDesktop}
              onPage={(page) => setQuery(setCatalogPage(query, page))}
              onPageSize={(size) => setQuery(setCatalogPageSize(query, size))}
            />
          </React.Fragment>
        )}
      </CatalogShell>

      <CatalogOverlays
        readinessModel={readinessModel}
        readinessPresentation={readinessPresentation}
        onCloseReadiness={() => setReadinessModel(null)}
        addModel={addModel}
        onCloseAdd={() => setAddModel(null)}
        onCreated={(id) => navigate(catalogAddedPath(id))}
        legendOpen={legendOpen}
        onLegendOpenChange={setLegendOpen}
      />
    </div>
  );
};

export default CatalogBrowseV2;
